import { Modal, SelectOption } from '@kep-ui-kit/ui-components';
import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import moment from 'moment';

import {
  DatePicker,
  StyledButton,
  StyledFormGroup,
  StyledLabel,
  StyledSelectMenu,
} from '@selfserve/react-ui';

import { useExtendSubscriptionDate } from '../data';
import { IContextData } from './admin-layout';
import { responseTypes } from '@selfserve/react-utils';

const StyledExtendModal = styled(Modal)`
  > .kepuikit-modal-container > .MuiPaper-root {
    background-color: transparent !important;
    box-shadow: none !important;
    height: 100%;
    justify-content: center;
    overflow: hidden;
  }
`;

const StyledExtendModalContent = styled('div')`
  display: flex;
  flex-direction: column;
  padding: 24px;
  background-color: #fff;
  border-radius: 4px;
  .kepuikit-base-input-root {
    height: 40px !important;
  }
`;

const StyledButtonContainer = styled('div')`
  margin-left: auto;
  button:first-of-type {
    margin-right: 16px;
  }
`;

interface Props {
  open: boolean;
  options: SelectOption[];
  handleClose: () => void;
  refetch: () => void;
}

export const AdminPlanExtendModal = ({
  options,
  handleClose,
  open,
  refetch,
}: Props) => {
  const { t } = useTranslation();
  const { openErrorModal } = useOutletContext<IContextData>() || {};
  const [extendPlanId, setExtendPlanId] = useState<string>('');
  const [extendDate, setExtendDate] = useState<any>(moment().add(1, 'days'));
  const { mutateAsync, isLoading, data, isError } =
    useExtendSubscriptionDate(extendPlanId);

  useEffect(() => {
    if (open) {
      setExtendPlanId(options[0].value);
    } else {
      setExtendPlanId('');
    }
  }, [open]);

  useEffect(() => {
    const { status } = data || {};
    if (status) {
      setExtendPlanId('');
      handleClose();
      if (isError || !String(status).startsWith(responseTypes.SUCCESS)) {
        openErrorModal();
      } else {
        refetch();
      }
    }
  }, [data]);

  const handleSetExtendPlan = useCallback((value: string) => {
    setExtendPlanId(value);
  }, []);

  const handleSetExtendDate = useCallback((value: any) => {
    setExtendDate(value);
  }, []);

  const handleSave = useCallback(() => {
    if (!isLoading) {
      mutateAsync({ date: extendDate.format() });
    }
  }, [extendPlanId, extendDate, isLoading]);

  return (
    <StyledExtendModal
      backdropColor="rgba(0,110,250,.8)"
      onClose={handleClose}
      open={open}
      fullWidth
    >
      <StyledExtendModalContent>
        <StyledFormGroup>
          <StyledSelectMenu
            fullWidth
            options={options}
            name="reason"
            inputLabel={t('common.plans')}
            defaultValue={extendPlanId}
            onChange={handleSetExtendPlan}
          />
        </StyledFormGroup>
        <StyledFormGroup largeMargin>
          <StyledLabel>{t('common.expiration-date')}</StyledLabel>
          <DatePicker
            fullWidth
            value={extendDate}
            onChange={handleSetExtendDate}
          />
        </StyledFormGroup>
        <StyledButtonContainer>
          <StyledButton isBorderless withoutMargin onClick={handleClose}>
            {t('common.cancel')}
          </StyledButton>
          <StyledButton withoutMargin loading={isLoading} onClick={handleSave}>
            {t('common.save')}
          </StyledButton>
        </StyledButtonContainer>
      </StyledExtendModalContent>
    </StyledExtendModal>
  );
};
